import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useQuery } from '@tanstack/react-query';
import { deskiesApi } from './deskiesApi';
import DeskiesTable from './DeskiesTable';
import DeskieActions from './DeskieActions';
import MissingMbtiBanner from './MissingMbtiBanner';
import { HSL, STATUS_COLORS } from './colors';

function StatusChip({ status }) {
  const config = STATUS_COLORS[status];
  if (!config) return null;
  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.bg,
        color: config.fg,
        fontWeight: 600,
        border: `1px solid ${config.fg}`,
      }}
    />
  );
}

function CenteredSpinner({ label }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        py: 6,
      }}
    >
      <CircularProgress sx={{ color: HSL.primary }} size={32} />
      <Typography variant="body2" sx={{ color: HSL.textMuted }}>
        {label}
      </Typography>
    </Box>
  );
}

function ErrorState({ onRetry }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '8px',
        border: `1px solid ${HSL.error}`,
        backgroundColor: HSL.errorSoft,
        p: 3,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ color: HSL.error, fontWeight: 600, mb: 1 }}
      >
        Failed to load deskies
      </Typography>
      <Typography variant="body2" sx={{ color: HSL.textStrong, mb: 2 }}>
        Something went wrong fetching the draft. Please retry.
      </Typography>
      <Button
        onClick={onRetry}
        variant="contained"
        sx={{
          backgroundColor: HSL.error,
          textTransform: 'none',
          '&:hover': { backgroundColor: 'hsl(0, 84%, 45%)' },
        }}
      >
        Retry
      </Button>
    </Paper>
  );
}

function EmptyState() {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '8px',
        border: `1px dashed ${HSL.border}`,
        backgroundColor: HSL.neutralSoft,
        p: 4,
        textAlign: 'center',
      }}
    >
      <CalendarTodayIcon sx={{ color: HSL.textMuted, fontSize: 36, mb: 1 }} />
      <Typography
        variant="subtitle1"
        sx={{ color: HSL.textStrong, fontWeight: 600, mb: 0.5 }}
      >
        Deskies not drafted yet
      </Typography>
      <Typography variant="body2" sx={{ color: HSL.textMuted }}>
        Deskies draft automatically at T+3 from the orientation date. Come back
        once the matcher has run — then preview the email and click{' '}
        <strong>Send emails</strong> to dispatch to learners.
      </Typography>
    </Paper>
  );
}

function DeskiesTab({ cohortId }) {
  const query = useQuery(
    ['deskies', cohortId],
    () => deskiesApi.list(cohortId),
    {
      enabled: !!cohortId,
      refetchInterval: (data) =>
        data?.pipeline_status === 'publishing' ? 3000 : false,
      refetchIntervalInBackground: false,
    }
  );

  if (query.isLoading) {
    return <CenteredSpinner label="Loading deskies…" />;
  }

  if (query.isError) {
    return <ErrorState onRetry={() => query.refetch()} />;
  }

  const data = query.data || {};
  const {
    pipeline_status: status,
    run_id: runId,
    missing_mbti: missingMbti,
    peer_groups: peerGroups,
  } = data;

  if (!status) {
    return <EmptyState />;
  }

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '8px',
          border: `1px solid ${HSL.border}`,
          backgroundColor: HSL.surface,
          p: 2,
          mb: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <StatusChip status={status} />
            {runId && (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: HSL.textMuted,
                  backgroundColor: HSL.neutralSoft,
                  px: 1,
                  py: 0.5,
                  borderRadius: '6px',
                }}
              >
                run {runId.slice(0, 8)}
              </Typography>
            )}
          </Stack>
          <DeskieActions cohortId={cohortId} status={status} />
        </Stack>
      </Paper>

      <MissingMbtiBanner count={missingMbti} />

      <DeskiesTable peerGroups={peerGroups} />
    </Box>
  );
}

export default DeskiesTab;
